"use client";

import { useMemo } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  XAxis,
  YAxis,
} from "recharts";

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";

import { useValueFormat } from "../../hooks/use-value-format";
import { WidgetEmpty } from "../widget-card";

import type { ChartColor, SeriesPayload } from "@/types";

// Backend ChartColor -> the app's own chart tokens (globals.css), the same set
// the analytics cards use. Colours arrive as an enum precisely so they can be
// resolved to theme-aware tokens here instead of arriving as raw CSS.
const COLORS: Record<ChartColor, string> = {
  green: "var(--color-tx-green)",
  red: "var(--color-tx-red)",
  blue: "var(--color-tx-blue)",
  orange: "var(--color-tx-orange)",
  yellow: "var(--color-tx-yellow)",
  purple: "var(--color-tx-purple)",
  cyan: "var(--color-tx-cyan)",
  gray: "var(--color-tx-gray)",
};

// Fallback palette for series that declare no colour.
const PALETTE: string[] = [
  COLORS.blue,
  COLORS.green,
  COLORS.orange,
  COLORS.purple,
  COLORS.cyan,
  COLORS.red,
];

/**
 * Line / bar / area charts — one renderer for every `series` widget.
 *
 * Knows nothing about earnings, orders or any particular widget: it plots
 * whatever categories and series the backend sent, formatting values with the
 * declared ValueFormat so money lands in the company's currency.
 */
export function SeriesWidget({ payload }: { payload: SeriesPayload }) {
  const format = useValueFormat();

  const colorFor = (color: ChartColor | null, index: number) =>
    (color ? COLORS[color] : undefined) ?? PALETTE[index % PALETTE.length];

  // Recharts wants row objects; the payload is column-oriented.
  const rows = useMemo(
    () =>
      payload.categories.map((category, index) => {
        const row: Record<string, string | number> = { category };
        for (const series of payload.series) {
          row[series.key] = series.values[index] ?? 0;
        }
        return row;
      }),
    [payload.categories, payload.series],
  );

  const config = useMemo<ChartConfig>(
    () =>
      Object.fromEntries(
        payload.series.map((series, index) => [
          series.key,
          { label: series.label, color: colorFor(series.color, index) },
        ]),
      ),
    [payload.series],
  );

  const headline =
    payload.total !== null ? format(payload.total, payload.value_format) : null;

  if (rows.length === 0) {
    return <WidgetEmpty label={payload.empty_text ?? "—"} />;
  }

  const axes = (
    <>
      <CartesianGrid vertical={false} strokeOpacity={0.2} />
      <XAxis
        dataKey="category"
        tickLine={false}
        axisLine={false}
        tickMargin={8}
        // Categories are raw — a month is 'YYYY-MM', a date is ISO — so they go
        // through the same formatter as values, in the company timezone.
        tickFormatter={(value: string) => format(value, payload.x_format)}
      />
      <YAxis
        tickLine={false}
        axisLine={false}
        width={64}
        // A count axis has to step in whole numbers: recharts' default ticks for
        // a domain of 0-3 are 0, 0.75, 1.5, 2.25, 3, which the integer formatter
        // then rounds into a ladder of repeats (0, 1, 2, 2, 3).
        allowDecimals={payload.value_format !== "integer"}
        tickFormatter={(value: number) => format(value, payload.value_format)}
      />
      <ChartTooltip
        content={
          <ChartTooltipContent
            labelFormatter={(label) => format(String(label), payload.x_format)}
            formatter={(value) => format(Number(value), payload.value_format)}
          />
        }
      />
    </>
  );

  return (
    <div className="flex flex-col gap-3">
      {headline && (
        <div className="flex items-baseline gap-2">
          <span className="font-display text-lg font-semibold text-foreground">
            {headline}
          </span>
          {payload.total_label && (
            <span className="text-sm text-fg-muted">{payload.total_label}</span>
          )}
        </div>
      )}

      <ChartContainer config={config} className="h-[240px] w-full">
        {payload.variant === "line" ? (
          <LineChart data={rows}>
            {axes}
            {payload.series.map((series, index) => (
              <Line
                key={series.key}
                dataKey={series.key}
                stroke={colorFor(series.color, index)}
                strokeWidth={2}
                dot={false}
              />
            ))}
          </LineChart>
        ) : payload.variant === "area" ? (
          <AreaChart data={rows}>
            {axes}
            {payload.series.map((series, index) => (
              <Area
                key={series.key}
                dataKey={series.key}
                stroke={colorFor(series.color, index)}
                fill={colorFor(series.color, index)}
                fillOpacity={0.15}
                strokeWidth={2}
                stackId={payload.stacked ? "stack" : undefined}
              />
            ))}
          </AreaChart>
        ) : (
          <BarChart data={rows}>
            {axes}
            {payload.series.map((series, index) => (
              <Bar
                key={series.key}
                dataKey={series.key}
                fill={colorFor(series.color, index)}
                radius={4}
                stackId={payload.stacked ? "stack" : undefined}
              />
            ))}
          </BarChart>
        )}
      </ChartContainer>
    </div>
  );
}
